import { Device } from "@capacitor/device";
import { merge } from "lodash";
import { debug } from "../App";
import { config } from "../app.config";
import StorageService from "./StorageService";

export default class ApiService {
  public static baseUrl: string = config.api;

  /**
   * Checks the current URL to see if it's a .belongmembership.com domain and if so, updates the API URL to use the production API
   * This is because the environment variables won't have been bundled in the web app
   * This could be implemented in a better way, but it's a quick fix for now
   * @protected
   */
  protected static async checkApiUrl() {
    const deviceInfo = await Device.getInfo();

    if (deviceInfo.platform === "web" && !config.api) {
      // are they browsing from a .belongmembership.com domain?
      const domain = window.location.hostname;
      const index = domain.indexOf(".belongmembership.com");
      if (index > 0) {
        const application = domain.substring(0, index);
        // if so, we need to update the config to use the production API because it won't have been bundled in the env variables
        ApiService.baseUrl = "https://belong-api-" + application + ".live.designbysweet.com/api";
      }
    }
  }

  public static async getAdminPanelUrl(): Promise<string> {
    await this.checkApiUrl();
    return ApiService.baseUrl.replace("/api", "/admin-authenticate");
  }

  public static async fetch<T = any>(path: string, options?: object, files?: boolean): Promise<T> {
    await this.checkApiUrl();
    path = path && path.match("^(http|https)://.*$") ? path : `${this.baseUrl}/${path.replace("/api/", "")}`;

    // Create the base api object
    const token = await this.getToken();
    const apiOptions: any = { headers: { Accept: "application/json", ...(!files && { "Content-Type": "application/json;charset=UTF-8" }) } };
    if (token) apiOptions.headers.Authorization = "Bearer " + token;
    const request = merge(apiOptions, options);

    // Handle json requests
    if (request.headers["Content-Type"] === false) {
      delete request.headers["Content-Type"];
    } else if (this.isJson(request)) {
      request.body = JSON.stringify(request.body);
    }

    debug("ApiService.fetch", path, request);

    return fetch(path, request).then(
      (res) => (res.ok || res.redirected ? (res.json() as T) : res.json().then((json) => Promise.reject(json))),
      (err) => Promise.reject(err),
    );
  }

  public static async get<T = any>(path: string, data: object = {}, options?: object): Promise<T> {
    const params = new URLSearchParams(Object.entries(data)).toString();
    return this.fetch<T>(path + (params ? "?" + params : ""), { method: "GET", ...options });
  }

  public static async post<T = any>(path: string, data: object = {}, options?: object, files?: boolean): Promise<T> {
    return this.fetch<T>(path, { method: "POST", body: data, ...options }, files);
  }

  public static async put(path: string, data: object = {}, options?: object, files?: boolean) {
    return this.fetch(path, { method: "PUT", body: data, ...options }, files);
  }

  public static async delete(path: string, data: object = {}, options?: object) {
    return this.fetch(path, { method: "DELETE", body: data, ...options });
  }

  public static getToken() {
    return StorageService.get({ key: "api_token" }).then((value) => value.value);
  }

  public static isJson(request: any) {
    return request && request.headers && (request.headers["Content-Type"] === "application/json" || request.headers["Content-Type"] === "application/json;charset=UTF-8");
  }

  public static setApiToken(token: string) {
    StorageService.set({ key: "api_token", value: token });
    StorageService.set({ key: "loggedIn", value: "true" });
  }
}

export type ApiResponse<T> = {
  data: T;
  // You might want to include other properties like status, message, etc.
};

export const fetcher = async <T>(url: string) => {
  //@ts-ignore
  const response = await ApiService.get<ApiResponse<T>>(url);
  return response.data;
};
